import { useState } from 'react'

import { MEASURE_PRODUCTS_PANE } from '@brickup/shared/constants'
import type {
  CatalogueDashboardResponse,
  CatalogueEntryRef,
} from '@brickup/shared/dto'
import { CatalogueKind } from '@brickup/shared/enums'
import { actorOf, HISTORY } from '@brickup/shared/locales/de/history'
import { PRODUCT_PROPERTY } from '@brickup/shared/locales/de/product-property'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { TERMS } from '@brickup/shared/locales/de/terms'
import { formatDate, formatDateTime } from '@brickup/shared/utils'
import { moreFooter } from './dashboard-links'
import { DashboardData, DashboardPanel, DashboardRow } from './DashboardLayout'
import { useCatalogueDashboard } from '@/api/staff-dashboard'
import { TONE_INK } from '@/components/base'
import {
  CATALOGUE_KIND_OPTIONS,
  catalogueLink,
} from '@/components/Internal/Catalogue/catalogue-filters'
import { CatalogueGlyph } from '@/components/Internal/Catalogue/CatalogueGlyph'
import { LabelValue } from '@/components/Internal/LabelValue'
import { LabelValueGroup } from '@/components/Internal/LabelValueGroup'
import { LinkRowList } from '@/components/Internal/LinkRow'
import { RankedList } from '@/components/Internal/RankedList'
import { SegmentedControl } from '@/components/SegmentedControl'
import { metaLine } from '@/utils/meta-line'

const COPY = STAFF_DASHBOARD.catalogue

/** The mark the catalogue tree gives a kind, for a row that names none. */
const kindGlyph = (kind: CatalogueKind) => (
  <CatalogueGlyph kind={kind} entry={{}} size={16} />
)

/**
 * The catalogue at a glance: what changed, what has gone untouched, what the
 * most depends on, what reaches nobody, and what is missing before it can be
 * quoted.
 */
export function CatalogueDashboard() {
  const query = useCatalogueDashboard()
  return (
    <DashboardData query={query}>
      {data => <Catalogue data={data} />}
    </DashboardData>
  )
}

function Catalogue({ data }: { data: CatalogueDashboardResponse }) {
  const { totals } = data

  return (
    <>
      <LabelValueGroup variant="tiles">
        <LabelValue
          label={TERMS.measurePlural}
          value={totals.measures}
          hint={COPY.measuresInConfigurator(totals.measuresInConfigurator)}
          to={catalogueLink(CatalogueKind.MEASURE)}
        />
        <LabelValue
          label={TERMS.productPlural}
          value={totals.products}
          hint={COPY.productsLinked(totals.productsLinked)}
          to={catalogueLink(CatalogueKind.PRODUCT)}
        />
        <LabelValue
          label={TERMS.formulaPlural}
          value={totals.formulas}
          hint={COPY.formulasUnused(totals.formulasUnused)}
          to={catalogueLink(CatalogueKind.FORMULA)}
        />
        <LabelValue
          label={COPY.incomplete}
          value={totals.incomplete}
          valueClassName={totals.incomplete > 0 ? TONE_INK.warning : undefined}
          hint={COPY.incompleteHint}
        />
        <LabelValue
          label={PRODUCT_PROPERTY.suggestions.title}
          value={totals.suggestedLinks}
          valueClassName={totals.suggestedLinks > 0 ? TONE_INK.info : undefined}
          hint={COPY.suggestedLinksHint(data.suggestedLinkMeasures.length)}
        />
      </LabelValueGroup>

      <DashboardRow columns="wide-narrow">
        <Changes data={data} />
        <DashboardPanel title={COPY.stalest} subtitle={COPY.stalestSubtitle}>
          <StaleList entries={data.stalest} />
        </DashboardPanel>
      </DashboardRow>

      <DashboardRow columns="halves">
        <MostUsed mostUsed={data.mostUsed} />
        <Unused unused={data.unused} />
      </DashboardRow>

      <DashboardRow columns="wide-narrow">
        <DashboardPanel
          title={COPY.suggestedLinks}
          subtitle={PRODUCT_PROPERTY.suggestions.hint}
        >
          <LinkRowList
            rows={data.suggestedLinkMeasures.map(measure => ({
              key: measure.id,
              title: measure.title,
              subtitle: COPY.waitingLinks(measure.count),
              // Straight to the tab that confirms them, the way the
              // notification about them lands there.
              to: catalogueLink(
                CatalogueKind.MEASURE,
                measure.id,
                MEASURE_PRODUCTS_PANE,
              ),
            }))}
            emptyTitle={PRODUCT_PROPERTY.suggestions.empty}
          />
        </DashboardPanel>

        <DashboardPanel title={COPY.quality}>
          <RankedList
            items={data.quality.map(row => ({
              key: row.issue,
              label: COPY.qualityIssues[row.issue],
              value: row.count,
            }))}
            emptyTitle={COPY.noIssues}
          />
        </DashboardPanel>
      </DashboardRow>

      <DashboardPanel
        title={COPY.stalePrices}
        subtitle={COPY.stalePricesSubtitle}
        footer={
          data.productsWithoutLink > 0
            ? COPY.withoutLink(data.productsWithoutLink)
            : undefined
        }
      >
        <LinkRowList
          rows={data.stalePrices.map(product => ({
            key: product.id,
            title: product.title,
            subtitle: product.checkedAt
              ? COPY.checkedAt(formatDate(product.checkedAt))
              : COPY.neverChecked,
            to: catalogueLink(CatalogueKind.PRODUCT, product.id),
          }))}
          emptyTitle={COPY.noLinkedProducts}
        />
      </DashboardPanel>
    </>
  )
}

/** Entries with their kind in front and the date they last moved under the
 *  name. */
function StaleList({
  entries,
}: {
  entries: (CatalogueEntryRef & { at: string })[]
}) {
  return (
    <LinkRowList
      rows={entries.map(entry => ({
        key: `${entry.kind}-${entry.id}`,
        leading: kindGlyph(entry.kind),
        title: entry.title,
        subtitle: COPY.changedAt(formatDate(entry.at)),
        to: catalogueLink(entry.kind, entry.id),
      }))}
      emptyTitle={COPY.nothingStale}
    />
  )
}

/**
 * The latest change to each entry — what was done, by whom and when — for the
 * whole team or for the reader alone.
 */
function Changes({ data }: { data: CatalogueDashboardResponse }) {
  const [scope, setScope] = useState<'all' | 'mine'>('all')
  const changes = scope === 'mine' ? data.myChanges : data.recentChanges

  return (
    <DashboardPanel
      title={COPY.recentChanges}
      action={
        <SegmentedControl
          options={[
            { value: 'all', label: COPY.changeScope.all },
            { value: 'mine', label: COPY.changeScope.mine },
          ]}
          value={scope}
          onChange={setScope}
        />
      }
    >
      <LinkRowList
        rows={changes.map(change => ({
          key: change.id,
          leading: kindGlyph(change.kind),
          title: change.label,
          subtitle: metaLine(
            HISTORY.actionLabels[change.action],
            actorOf(change.actorName, change.actorEmail),
            formatDateTime(change.at),
          ),
          to:
            change.entryId != null
              ? catalogueLink(change.kind, change.entryId)
              : undefined,
        }))}
        emptyTitle={scope === 'mine' ? COPY.noChangesOfMine : COPY.noChanges}
      />
    </DashboardPanel>
  )
}

/** The control a panel is read one catalogue kind at a time through. */
function useKindTabs() {
  const [kind, setKind] = useState<CatalogueKind>(CatalogueKind.MEASURE)
  return {
    kind,
    action: (
      <SegmentedControl
        options={CATALOGUE_KIND_OPTIONS}
        value={kind}
        onChange={setKind}
      />
    ),
  }
}

function MostUsed({
  mostUsed,
}: {
  mostUsed: CatalogueDashboardResponse['mostUsed']
}) {
  const { kind, action } = useKindTabs()

  return (
    <DashboardPanel title={COPY.mostUsed} action={action}>
      <RankedList
        items={mostUsed[kind].map(entry => ({
          key: String(entry.id),
          label: entry.title,
          value: entry.uses,
          to: catalogueLink(entry.kind, entry.id),
        }))}
        format={COPY.uses}
        emptyTitle={COPY.nothingUsed}
      />
    </DashboardPanel>
  )
}

function Unused({ unused }: { unused: CatalogueDashboardResponse['unused'] }) {
  const { kind, action } = useKindTabs()
  const shown = unused[kind]

  return (
    <DashboardPanel
      title={TERMS.unused}
      action={action}
      footer={moreFooter(shown.count, shown.entries.length)}
    >
      <LinkRowList
        rows={shown.entries.map(entry => ({
          key: entry.id,
          title: entry.title,
          to: catalogueLink(entry.kind, entry.id),
        }))}
        emptyTitle={COPY.nothingUnused}
      />
    </DashboardPanel>
  )
}
